import { Space, Switch, Tooltip, Typography, message } from "antd";
import { useAppDispatch, useAppSelector } from "hooks";
import React from "react";
import { clearDuplicateSearchBirthResult, getDuplicateSearchSettingsRequest, setDuplicateSearchBirthRequest } from "store/duplicateSearchSettings/actions";

interface IBirthToggleProps {
    // The duplicate search (its find job id).
    definitionId: string;
}

const HELP =
    "When on, deduplication creates a golden record from each cluster that has none, in the schema's golden dataset. Off by default; one duplicate search per schema can have it.";

// Whether a duplicate search creates golden records (births), for a search of one schema. Shown to
// a tenant with Golden Nominal maintenance on; with it off, only on a search that still has births
// on, so they can be turned off (and the schema freed for another search).
const BirthToggle = ({ definitionId }: IBirthToggleProps) => {
    const dispatch = useAppDispatch();
    const goldenMaintenanceOn = useAppSelector(
        (state) => !!(state.userLogin.tenant as { features?: { goldenMaintenance?: boolean } } | undefined)?.features?.goldenMaintenance,
    );
    const settings = useAppSelector((state) => state.duplicateSearchSettings.settingsByDefinition[definitionId]);
    const settingBirth = useAppSelector((state) => !!state.duplicateSearchSettings.settingBirth[definitionId]);
    // Only this search's outcome is shown here.
    const setBirthResult = useAppSelector((state) => {
        const result = state.duplicateSearchSettings.setBirthResult;
        return result && result.definitionId === definitionId ? result : null;
    });
    // Changing it needs the duplicate search "edit" permission; others see it read-only.
    const canEdit = useAppSelector((state) => !!(state.dataObjectDuplicateSearch.userSearchDataObjectsPermision as { canEdit?: boolean } | null)?.canEdit);

    React.useEffect(() => {
        if (definitionId) {
            dispatch(getDuplicateSearchSettingsRequest(definitionId));
        }
    }, [dispatch, definitionId]);

    // Shown once, then cleared.
    React.useEffect(() => {
        if (!setBirthResult) {
            return;
        }
        if (setBirthResult.success) {
            message.success(setBirthResult.birthEnabled ? "This search now creates golden records." : "This search no longer creates golden records.");
        } else {
            message.error(setBirthResult.message || "Golden record creation could not be changed.");
        }
        dispatch(clearDuplicateSearchBirthResult());
    }, [dispatch, setBirthResult]);

    if (!settings || !settings.birthAvailable || (!goldenMaintenanceOn && !settings.birthEnabled)) {
        return null;
    }
    // With maintenance off they can only be turned off.
    const disabled = !canEdit || (!goldenMaintenanceOn && !settings.birthEnabled);
    return (
        <Tooltip title={HELP}>
            <Space data-testid="birth-toggle">
                <Switch
                    size="small"
                    checked={settings.birthEnabled}
                    loading={settingBirth}
                    disabled={disabled}
                    onChange={(checked) => dispatch(setDuplicateSearchBirthRequest(definitionId, checked))}
                    data-testid="birth-toggle-switch"
                />
                <Typography.Text>Create golden records</Typography.Text>
            </Space>
        </Tooltip>
    );
};

export default BirthToggle;
